import type {
    TableCellExpandedProps,
    TableCellProps,
    TableHeaderProps,
} from '@ksyos/design-system/dist/components/table';
import {
    Radio,
    Table,
    TableBody,
    TableCell,
    TableCellExpanded,
    TableCellExpandRow,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderSelectAll,
    TableRow,
    TableRowExpanded,
} from '@ksyos/design-system';
import { xor } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { SortDirection, toUpperCase } from '../../utils/sortDirection';

export type ITableHeader = { columnKey: string } & Omit<
    TableHeaderProps,
    'isSortHeader' | 'sortDirection' | 'scope' | 'onClick'
>;

export interface IExpandedTableRow {
    cells: TableCellExpandedProps[];
    isNotSelectable?: boolean;
    rowKey: string;
}

export interface ITableRow {
    cells: TableCellProps[];
    expandedTableRows?: IExpandedTableRow[];
    isNotSelectable?: boolean;
    rowKey: string;
}

export interface ISelectableSortableTableProps {
    isChildSelectionEnabled?: boolean;
    isReadOnly?: boolean;
    isSingleSelection?: boolean;
    onSelect: (selectedRowKeys: string[]) => Promise<void>;
    onSort: (sortKey: string, sortDirection: SortDirection) => Promise<void>;
    selectedRowKeys: string[];
    sortDirection: SortDirection;
    sortKey: string;
    tableHeaders: ITableHeader[];
    tableRows: ITableRow[];
}

export function SelectableSortableTable(props: ISelectableSortableTableProps) {
    const [rowKeys, setRowKeys] = useState<string[]>([]);
    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    useEffect(() => {
        setRowKeys(props.tableRows.map((row) => row.rowKey));
    }, [props.tableRows]);

    const hasExpandableRows = props.tableRows.some((row) => row.expandedTableRows);

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {renderHeaderSelectAll()}
                        {hasExpandableRows && <TableHeader key={'expandTableHeader'} />}
                        {props.tableHeaders.map((tableHeader) => {
                            if (tableHeader.isSortable) {
                                return renderSortableHeaderColumn(tableHeader);
                            } else {
                                return (
                                    <TableHeader
                                        {...tableHeader}
                                        key={tableHeader.columnKey}
                                    />
                                );
                            }
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {props.tableRows.map((row) => {
                        const isExpanded = expandedRows.includes(row.rowKey);

                        return (
                            <Fragment key={row.rowKey}>
                                <TableRow>
                                    {renderSelectionCell(row)}
                                    {hasExpandableRows && (
                                        <TableCellExpandRow
                                            isExpandable
                                            isExpanded={isExpanded}
                                            onExpand={(event) => {
                                                event.stopPropagation();
                                                setExpandedRows(xor(expandedRows, [row.rowKey]));
                                            }}
                                        />
                                    )}
                                    {row.cells.map((cell, i) => {
                                        const columnKey = props.tableHeaders[i]?.columnKey || 'unknown';

                                        return (
                                            <TableCell
                                                {...cell}
                                                key={`${columnKey}_${row.rowKey}`}
                                            />
                                        );
                                    })}
                                </TableRow>

                                {row.expandedTableRows?.map((expandedRow) => {
                                    return (
                                        <TableRowExpanded
                                            isExpanded={isExpanded}
                                            key={expandedRow.rowKey}
                                        >
                                            {renderChildSelectionCell(row, expandedRow)}
                                            <TableCellExpanded />
                                            {expandedRow.cells.map((expandedCell, i) => {
                                                const columnKey = props.tableHeaders[i]?.columnKey || 'unknown';

                                                return (
                                                    <TableCellExpanded
                                                        {...expandedCell}
                                                        key={`expandedCell_${columnKey}_${row.rowKey}`}
                                                    />
                                                );
                                            })}
                                        </TableRowExpanded>
                                    );
                                })}
                            </Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );

    function renderSortableHeaderColumn(sortableTableHeaderProps: ITableHeader) {
        return (
            <TableHeader
                isSortable
                isSortHeader={props.sortKey === sortableTableHeaderProps.columnKey}
                key={sortableTableHeaderProps.columnKey}
                onClick={async () => {
                    const sortDirection =
                        props.sortKey === sortableTableHeaderProps.columnKey
                            ? props.sortDirection === SortDirection.asc
                                ? SortDirection.desc
                                : SortDirection.asc
                            : SortDirection.asc;
                    await props.onSort(sortableTableHeaderProps.columnKey, sortDirection);
                }}
                sortDirection={
                    props.sortKey === sortableTableHeaderProps.columnKey ? toUpperCase(props.sortDirection) : undefined
                }
            >
                {sortableTableHeaderProps.children}
            </TableHeader>
        );
    }

    function renderSelectionCell(row: ITableRow, isExpanded?: boolean) {
        if (props.isReadOnly) {
            return;
        }

        if (row.isNotSelectable) {
            if (isExpanded) {
                return <TableCellExpanded style={{ width: '68px' }} />;
            } else {
                return <TableCell style={{ width: '68px' }} />;
            }
        }

        if (props.isSingleSelection) {
            const radio = (
                <Radio
                    checked={props.selectedRowKeys.includes(row.rowKey)}
                    id={row.rowKey}
                    label=""
                    onChange={() => {
                        handleSelection(row);
                    }}
                    value={row.rowKey}
                />
            );
            if (isExpanded) {
                return (
                    <TableCellExpanded
                        isSelected={!props.isReadOnly && props.selectedRowKeys.includes(row.rowKey)}
                        style={{ width: '68px' }}
                    >
                        {radio}
                    </TableCellExpanded>
                );
            } else {
                return (
                    <TableCell
                        isSelected={!props.isReadOnly && props.selectedRowKeys.includes(row.rowKey)}
                        style={{ width: '68px' }}
                    >
                        {radio}
                    </TableCell>
                );
            }
        } else {
            return (
                <TableCellSelectRow
                    isSelected={!props.isReadOnly && props.selectedRowKeys.includes(row.rowKey)}
                    onSelect={() => {
                        if (!props.isReadOnly) {
                            handleSelection(row);
                        }
                    }}
                />
            );
        }
    }

    function renderHeaderSelectAll() {
        if (props.isReadOnly) {
            return;
        }

        if (props.isSingleSelection) {
            return <TableHeader />;
        } else {
            return (
                <TableHeaderSelectAll
                    checked={!props.isReadOnly && props.selectedRowKeys.length > 0}
                    indeterminate={props.selectedRowKeys.length > 0 && props.selectedRowKeys.length < rowKeys.length}
                    onSelect={() => {
                        const currentSelectedRows: string[] = [];
                        if (!props.isReadOnly && props.selectedRowKeys.length < rowKeys.length) {
                            currentSelectedRows.push(...rowKeys.map((key) => key));
                        }
                        props.onSelect(currentSelectedRows);
                    }}
                />
            );
        }
    }

    function renderChildSelectionCell(row: ITableRow, expandedRow: IExpandedTableRow) {
        if (props.isChildSelectionEnabled) {
            return renderSelectionCell(expandedRow, true);
        } else {
            return <TableCellExpanded isSelected={!props.isReadOnly && props.selectedRowKeys.includes(row.rowKey)} />;
        }
    }

    function handleSelection(row: ITableRow) {
        if (row.isNotSelectable) {
            return;
        }

        if (props.isSingleSelection && !props.isReadOnly) {
            props.onSelect([row.rowKey]);
        } else if (!props.isReadOnly) {
            props.onSelect(xor(props.selectedRowKeys, [row.rowKey]));
        }
    }
}
